import {
  LEAD_STAGE_LABEL,
  LEAD_STATUS_FECHADOS,
} from '@modules/crm/constants/Text'
import { aawzDate } from '@utils/dates/aawz-date'
// Classes globais já compiladas (src/design/helpers/_text.sass) — evitam o
// mesmo problema que var(--warning-new) tinha (custom property injetada só
// em runtime, não confiável no primeiro paint). Ver leadEtapaStatus.js.
const CONTADOR_SINALIZADOS_CLASSES = {
  atencao: 'text-warning-yellow',
  critico: 'text-red',
}

function cloneOwner(owner) {
  if (!owner || typeof owner !== 'object') {
    return null
  }

  const id = owner.id
  const name = owner.name

  if (!id && !name) {
    return null
  }

  return {
    id,
    name,
  }
}

export function normalizeLead(rawLead) {
  if (!rawLead) {
    return null
  }

  const stage = rawLead.stage || 'contact'
  const stageLabelFromConstant = LEAD_STAGE_LABEL[stage]
  const normalizedTasks = Array.isArray(rawLead.tasks) ? rawLead.tasks : []
  const normalizedOpportunities = Array.isArray(rawLead.opportunities)
    ? rawLead.opportunities
    : []

  return {
    ...rawLead,
    owner: cloneOwner(rawLead.owner),
    stage,
    stageLabel: stageLabelFromConstant || rawLead.stageLabel || stage,
    tasks: normalizedTasks.map((task) => ({ ...task })),
    opportunities: normalizedOpportunities.map((opportunity) => ({
      ...opportunity,
    })),
  }
}

export function cloneLeads(leads) {
  if (!Array.isArray(leads)) {
    return []
  }

  return leads
    .map((lead) => normalizeLead(lead))
    .filter((lead) => Boolean(lead))
}

export function getActiveFunil(funis = []) {
  if (!Array.isArray(funis) || !funis.length) {
    return {}
  }

  const active = funis.find((f) => f && f.ativo)

  return active || {}
}

export const SELECTED_FUNIL_STORAGE_KEY = 'crm_selected_funil_id'

export function getStoredFunilId() {
  try {
    return window.localStorage.getItem(SELECTED_FUNIL_STORAGE_KEY) || null
  } catch (e) {
    console.warn('[crm-leads] localStorage indisponível ao ler o funil selecionado', e)
    return null
  }
}

export function setStoredFunilId(id) {
  try {
    if (id) {
      window.localStorage.setItem(SELECTED_FUNIL_STORAGE_KEY, id)
    } else {
      window.localStorage.removeItem(SELECTED_FUNIL_STORAGE_KEY)
    }
  } catch (e) {
    // localStorage indisponível (modo privado) — segue sem persistir.
    console.warn('[crm-leads] localStorage indisponível ao salvar o funil selecionado', e)
  }
}

// Nome do último funil selecionado — exibido no loading inicial do seletor
// (antes dos funis carregarem) pra não mostrar "Selecionar funil" piscando.
export const SELECTED_FUNIL_NOME_STORAGE_KEY = 'crm_selected_funil_nome'

export function getStoredFunilNome() {
  try {
    return window.localStorage.getItem(SELECTED_FUNIL_NOME_STORAGE_KEY) || ''
  } catch (e) {
    console.warn('[crm-leads] localStorage indisponível ao ler o nome do funil', e)
    return ''
  }
}

export function setStoredFunilNome(nome) {
  try {
    if (nome) {
      window.localStorage.setItem(SELECTED_FUNIL_NOME_STORAGE_KEY, nome)
    } else {
      window.localStorage.removeItem(SELECTED_FUNIL_NOME_STORAGE_KEY)
    }
  } catch (e) {
    // localStorage indisponível — segue sem persistir.
    console.warn('[crm-leads] localStorage indisponível ao salvar o nome do funil', e)
  }
}

// Funil selecionado: por `selectedId` se existir na lista; senão cai pro funil
// ativo; senão o primeiro. Base do seletor de funil do pipeline de leads.
export function getSelectedFunil(funis = [], selectedId = null) {
  if (!Array.isArray(funis) || !funis.length) {
    return {}
  }

  if (selectedId) {
    const found = funis.find((f) => f && f.id === selectedId)
    if (found) return found
  }

  // Honra o funil ativo mesmo sem `id` (mantém o comportamento do getActiveFunil
  // anterior). getActiveFunil retorna {} quando não há ativo → cai no funis[0].
  const active = getActiveFunil(funis)
  if (active && Object.keys(active).length) return active

  return funis[0] || {}
}

// Resolve qual funil deve ficar selecionado ao carregar a página, sem
// depender de nenhum componente ter montado ainda: mantém o `currentSelectedId`
// se ele já é válido entre os funis carregados; senão restaura o último funil
// persistido em localStorage (SELECTED_FUNIL_STORAGE_KEY) ou, na ausência
// dele, o funil ativo/primeiro (via getSelectedFunil). Usado tanto pelo
// FunilSelector (ao montar, dentro do popover de Filtros) quanto pelo
// CrmLeadsFilters (sempre montado, resolve o funil ANTES do popover abrir —
// bug real: sem isso, o Kanban mostrava o funil ativo da API em vez do
// último escolhido pelo usuário até o popover de Filtros ser aberto).
export function resolveInitialFunilId(funis = [], currentSelectedId = null) {
  if (!Array.isArray(funis) || !funis.length) return null

  const atualValido =
    currentSelectedId && funis.some((f) => f && f.id === currentSelectedId)
  if (atualValido) return currentSelectedId

  const alvo = getSelectedFunil(funis, getStoredFunilId())
  return (alvo && alvo.id) || null
}

// Resolve + aplica (dispatch no store + persiste id/nome) o funil inicial,
// tudo num só import. `store` é o $store da instância chamadora (dispatch
// não depende de `this`, então funciona igual vindo de qualquer componente).
// No-op quando não há funis ainda ou quando o id atual já é válido.
export function applyInitialFunilSelection(store, funis, currentSelectedId) {
  const alvo = resolveInitialFunilId(funis, currentSelectedId)
  if (!alvo || alvo === currentSelectedId) return

  store.dispatch('crm/setSelectedFunilId', alvo)
  setStoredFunilId(alvo)
  const funil = funis.find((f) => f && f.id === alvo)
  if (funil) setStoredFunilNome(funil.nome)
}

export function getFunisFromState(state) {
  const data = state && state.funilSteps && state.funilSteps.data
  if (!data) return []
  if (Array.isArray(data.funis)) return data.funis
  if (Array.isArray(data)) return data
  return []
}

export function findEtapaInFunil(funil, idEtapa) {
  if (!funil || !Array.isArray(funil.etapas)) return null
  return funil.etapas.find((e) => e && e.id === idEtapa) || null
}

export function normalizeLeadId(lead = {}) {
  return (lead && (lead.id_lead || lead.id)) || null
}

/**
 * Indica se um status de lead o tira do pipeline (Ganho/Perda) — usado pelo
 * kanban de etapas do funil para mover o card pra `funil.leadsFechados`
 * em vez de deixá-lo na etapa depois de ganho/perdido.
 */
export function isLeadFechado(status) {
  return LEAD_STATUS_FECHADOS.includes(status)
}

/**
 * Rótulo pluralizado da contagem de leads visíveis de uma coluna do kanban
 * (cards já filtrados em `column.data`): "1 lead", "2 leads", "0 leads".
 * Retorna "0 leads" para coluna nula ou `data` ausente/inválido.
 * Reaproveitado nos cabeçalhos do kanban de funil e do kanban por status.
 */
export function rotuloContagemLeads(column) {
  const count = column && Array.isArray(column.data) ? column.data.length : 0

  return `${count} ${count === 1 ? 'lead' : 'leads'}`
}

/**
 * Soma o `valor_estimado` de uma lista de leads, ignorando valores
 * ausentes ou não numéricos. Usado para recalcular otimisticamente o
 * `metricas.valor_total` de uma etapa do kanban quando um lead é editado,
 * movido ou adicionado — sem precisar refazer a request do funil.
 */
export function somarValorEstimadoLeads(leads) {
  if (!Array.isArray(leads)) return 0

  return leads.reduce((acc, lead) => {
    const valor = Number(lead && lead.valor_estimado)
    return acc + (Number.isNaN(valor) ? 0 : valor)
  }, 0)
}

/**
 * Conta, entre os leads visíveis de uma coluna do kanban, quantos estão
 * sinalizados em cada nível (`status_etapa.nivel`, ver `leadEtapaStatus.js`).
 * Leads sem `status_etapa` ou com `nivel: 'ok'` não entram em nenhuma
 * contagem.
 */
export function contarLeadsSinalizados(leads) {
  const lista = Array.isArray(leads) ? leads : []

  return lista.reduce(
    (acc, lead) => {
      const nivel = lead && lead.status_etapa && lead.status_etapa.nivel
      if (nivel === 'atencao') acc.atencao += 1
      if (nivel === 'critico') acc.critico += 1
      return acc
    },
    { atencao: 0, critico: 0 }
  )
}

/**
 * Monta as partes (contagem + classe de cor + texto do tooltip) do badge de
 * sinalizados exibido no canto do header da coluna do kanban. Cada parte vira
 * um badge compacto (ícone + número), nunca uma linha de texto nova — badge
 * é posicionado em `position: absolute` no canto do header (ver
 * view-crm-leads.vue) exatamente para não alterar a altura do cabeçalho e
 * gerar disparidade visual entre colunas com e sem lead sinalizado. Retorna
 * array vazio quando nenhum lead da coluna está sinalizado (S4,
 * REQUIREMENTS.md crm-funil-etapa-sla-flag).
 */
export function buildContadorSinalizadosParts(leads) {
  const { atencao, critico } = contarLeadsSinalizados(leads)
  const parts = []

  if (atencao > 0) {
    parts.push({
      nivel: 'atencao',
      count: atencao,
      className: CONTADOR_SINALIZADOS_CLASSES.atencao,
      text: `${atencao} em atenção`,
    })
  }

  if (critico > 0) {
    parts.push({
      nivel: 'critico',
      count: critico,
      className: CONTADOR_SINALIZADOS_CLASSES.critico,
      text: `${critico} ${critico === 1 ? 'crítico' : 'críticos'}`,
    })
  }

  return parts
}

/**
 * Reconstrói `status_etapa` otimisticamente quando um lead É MOVIDO de
 * etapa no kanban (drag-and-drop). O backend zera `data_entrada_etapa`
 * no momento do move (ver `APICrmLead._hook_pre_edit`), então o lead
 * sempre entra na etapa destino com `dias_na_etapa: 0` e `nivel: 'ok'` —
 * sem isso, o card ficava com o `status_etapa` (cor/badge) da etapa
 * ANTERIOR até um F5 recarregar o payload real do backend.
 */
export function buildResetStatusEtapaAoMover(etapaDestino) {
  const config = (etapaDestino && etapaDestino.configuracoes) || {}
  return {
    dias_na_etapa: 0,
    nivel: 'ok',
    limite_atencao: config.dias_atencao === undefined ? null : config.dias_atencao,
    limite_critico: config.dias_critico === undefined ? null : config.dias_critico,
    data_entrada_etapa: aawzDate().format('DD/MM/YYYY HH:mm'),
  }
}

/**
 * Shallow clone das colunas do kanban — array nova (pra vue-draggable
 * poder mutar via splice/push sem tocar no store) preservando refs
 * vivos dos lead objects. Substitui `JSON.parse(JSON.stringify(...))`
 * que clonava também os leads e quebrava a reatividade automática
 * sobre `lead.X` (forçando re-clone full a cada edição).
 *
 * Custo: O(stages + totalCards) em vez de O(stages + totalCards × fields).
 */
export function shallowCloneColumns(columnsData = []) {
  if (!Array.isArray(columnsData)) return []
  return columnsData.map((col) => ({
    ...col,
    data: Array.isArray(col && col.data) ? [...col.data] : [],
  }))
}
